import React from "react";
import { useLanguage } from "@/i18n";

const SKILLS = [
  "Conceptual & computational design",
  "Healthcare architecture",
  "BIM coordination & detailing",
  "Hand sketching & visualisation",
  "Mental-health & accessible spaces",
];

const TOOLS = ["Rhino", "Grasshopper", "Dynamo", "Revit", "AutoCAD", "Adobe Suite", "Hand"];

export const AboutMeContent = () => {
  const { isArabic } = useLanguage();
  const skills = isArabic ? [
    "تصميم مفاهيمي وحاسوبي",
    "عمارة الرعاية الصحية",
    "تنسيق BIM والتفاصيل التنفيذية",
    "الرسم اليدوي والتصور المعماري",
    "مساحات الصحة النفسية وإتاحة الوصول",
  ] : SKILLS;

  return (
  <div className="grid md:grid-cols-[140px_1fr] gap-5 items-start">
    <div className="relative">
      <div
        className="w-32 h-32 rounded-full border border-[var(--paper-edge)] bg-[var(--bg-board)] grid place-items-center overflow-hidden mx-auto md:mx-0"
        aria-label="Portrait placeholder"
      >
        <svg viewBox="0 0 100 100" className="w-full h-full text-ink/60">
          <circle cx="50" cy="38" r="14" fill="none" stroke="currentColor" strokeWidth="0.8" />
          <path d="M22 86 C28 66 72 66 78 86" fill="none" stroke="currentColor" strokeWidth="0.8" />
          <line x1="14" y1="14" x2="22" y2="22" stroke="currentColor" strokeWidth="0.5" strokeDasharray="2 2" />
          <line x1="86" y1="14" x2="78" y2="22" stroke="currentColor" strokeWidth="0.5" strokeDasharray="2 2" />
        </svg>
      </div>
      <span className="font-hand absolute -top-3 -right-2 md:left-24 md:right-auto text-accentRed text-lg rotate-[-6deg]">
        مرحبًا
      </span>
    </div>

    <div>
      <p className="text-ink leading-relaxed">
        {isArabic ? (
          <>
            أنا <strong className="font-display">عبدالرحمن العريفي</strong>، خريج RIBA Part 1 من جامعة برمنغهام سيتي،
            وأعمل حاليًا على مرحلة Part 2 بالتوازي مع عملي كمساعد معماري في مجال الرعاية الصحية لدى
            <em> Pinnegar Hayward Design</em> في برمنغهام. أتنقل بين الفن والعمارة والكود، ويشدني المستقبل من خلال
            التصميم المفاهيمي والحاسوبي.
          </>
        ) : (
          <>
            I'm <strong className="font-display">Abdulrahman Alarifi</strong> — a RIBA Part 1 graduate
            from Birmingham City University, currently working on my Part 2 alongside practice as a healthcare
            architecture assistant at <em>Pinnegar Hayward Design</em> in Birmingham. I move between art,
            architecture and code, and I'm drawn to the future — designing through conceptual and
            computational methods.
          </>
        )}
      </p>

      <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-4">
        <div>
          <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-ink-faint mb-2">{isArabic ? "المنهج" : "Approach"}</p>
          <p className="text-sm text-ink-soft leading-relaxed">
            {isArabic ? "جريء ومفاهيمي وفيه شيء من الاستشراف. أتعامل مع الحوسبة كشريك في التصميم، لا كأداة نهائية فقط." : "Bold, conceptual, and a little speculative. Computation as a design partner — not a finishing tool."}
          </p>
        </div>
        <div>
          <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-ink-faint mb-2">{isArabic ? "الخلفية" : "Background"}</p>
          <p className="text-sm text-ink-soft leading-relaxed">
            {isArabic ? "بكالوريوس عمارة · جامعة الملك سعود، 2018" : "B.Arch · King Saud University, 2018"}<br />
            {isArabic ? "BA Architecture (Part 1) · BCU، 2022" : "BA Architecture (Pt 1) · BCU, 2022"}<br />
            {isArabic ? "M.Arch (Part 2) · BCU، مستمر" : "M.Arch (Pt 2) · BCU, ongoing"}
          </p>
        </div>
        <div>
          <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-ink-faint mb-2">{isArabic ? "المهارات" : "Skills"}</p>
          <ul className="space-y-1 text-sm text-ink-soft">
            {skills.map((s) => (
              <li key={s} className="flex gap-2"><span className="text-accentRed">·</span>{s}</li>
            ))}
          </ul>
        </div>
        <div>
          <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-ink-faint mb-2">{isArabic ? "الأدوات" : "Tools"}</p>
          <div className="flex flex-wrap gap-1.5">
            {TOOLS.map((t) => (
              <span key={t} className="font-mono text-[10px] uppercase tracking-[0.15em] px-2 py-0.5 border border-[var(--paper-edge)] text-ink-soft">
                {t}
              </span>
            ))}
          </div>
          <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-ink-faint mt-4 mb-1">{isArabic ? "اللغات" : "Languages"}</p>
          <p className="text-sm text-ink-soft">{isArabic ? "العربية (لغة أم) · الإنجليزية (بطلاقة)" : "Arabic (native) · English (fluent)"}</p>
        </div>
      </div>

      <div className="mt-6 pt-4 border-t border-dashed border-[var(--paper-edge)] flex justify-end">
        <span className="font-hand text-3xl text-accentBlue rotate-[-4deg]">— Abdulrahman</span>
      </div>
    </div>
  </div>
  );
};
